/**
 * usePreloadMessageAssets — Keeps a rolling window of preloaded assets ahead
 * of the currently visible message.
 *
 * On phase start, the first batch of messages is enqueued at high priority so
 * gameplay can begin immediately. As the player advances, upcoming messages are
 * enqueued at low priority to stay ahead without starving other loads.
 *
 * The window size (LOOKAHEAD) controls how many messages ahead we preload.
 * A value of 3 means we always try to have the next 3 messages' assets ready.
 */

import { useEffect, useRef } from "react";
import { useGameStore } from "@/state/gameStore";
import { useCurrentPhase } from "@/state/selectors";
import { RoomVariant } from "@/types/state";
import {preloadManager} from "@/libs/assetPreloading/PreloadManager";
import {CategorizedImages, getCategorizedImagesFromMessages} from "@/libs/assetPreloading/categorizedImages";


/** How many messages ahead of the current position to preload */
const LOOKAHEAD = 3;

/**
 * Enqueue all images from a CategorizedImages set with the given priority.
 */
function enqueueCategorized(images: CategorizedImages, priority: "high" | "low") {
    if (images.lg.length > 0) {
        preloadManager.enqueueImages(images.lg, "lg", priority);
    }
    if (images.md.length > 0) {
        preloadManager.enqueueImages(images.md, "md", priority);
    }
    if (images.sm.length > 0) {
        preloadManager.enqueueImages(images.sm, "sm", priority);
    }
}

export function usePreloadMessageAssets(selectedRoom: RoomVariant | null): void {
    const phase = useCurrentPhase();
    const messageQueue = useGameStore((s) => s.messageQueue);
    const currentMessage = useGameStore((s) => s.currentMessage);

    /**
     * Track which messages we've already enqueued by documentId,
     * so we never enqueue the same message's assets twice.
     * Using documentId instead of referential identity (indexOf) because
     * Immer/Zustand may produce new object references on state updates.
     * Reset when the phase changes.
     */
    const enqueuedIdsRef = useRef<Set<string>>(new Set());
    const currentPhaseIdRef = useRef<string | null>(null);

    // Reset tracking when phase changes and clean up preload links from previous phase
    useEffect(() => {
        const phaseId = phase?.documentId ?? null;
        if (phaseId !== currentPhaseIdRef.current) {
            // Remove all image preload links from the previous phase.
            // Already-rendered images remain in the browser's HTTP cache.
            preloadManager.clearAllImagePreloads();
            currentPhaseIdRef.current = phaseId;
            enqueuedIdsRef.current = new Set();
        }
    }, [phase?.documentId]);

    // Preload rolling window
    useEffect(() => {
        try {
            if (!selectedRoom || !phase?.messages) return;

            const messagesToPreload = [];

            if (currentMessage) {
                messagesToPreload.push(currentMessage);
            }

            for (let i = 0; i < Math.min(LOOKAHEAD, messageQueue.length); i++) {
                messagesToPreload.push(messageQueue[i]);
            }

            if (messagesToPreload.length === 0) return;

            // Filter to only messages we haven't enqueued yet, using documentId
            const newMessages = messagesToPreload.filter((msg) => {
                const id = msg?.documentId;
                if (!id || enqueuedIdsRef.current.has(id)) return false;
                enqueuedIdsRef.current.add(id);
                return true;
            });

            if (newMessages.length === 0) return;

            const categorized = getCategorizedImagesFromMessages(newMessages, selectedRoom);

            // First batch of a phase (nothing enqueued yet except what we just added)
            // gets high priority so gameplay can start. Subsequent batches are low.
            const isInitialBatch = enqueuedIdsRef.current.size <= messagesToPreload.length;
            const priority = isInitialBatch ? "high" : "low";

            enqueueCategorized(categorized, priority);
        } catch (err) {
            // Preload failures must never break gameplay — log and move on
            console.warn("Failed to enqueue message assets for preloading:", err);
        }
    }, [currentMessage, messageQueue, selectedRoom, phase]);
}